import React from "react";
import styled, { keyframes, css } from "styled-components";

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
`;

const Controls = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: center;
`;

const VoiceBtn = styled.button<{ active: boolean; danger?: boolean }>`
  background: ${({ active, danger }) =>
    danger
      ? "var(--color-red)"
      : active
        ? "#22c55e"
        : "rgba(255,255,255,0.15)"};
  border: 2px solid
    ${({ active, danger }) =>
      danger
        ? "var(--color-red)"
        : active
          ? "#22c55e"
          : "rgba(255,255,255,0.3)"};
  border-radius: 50%;
  width: 4.4rem;
  height: 4.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 2rem;
  transition: all 0.2s;
  color: var(--color-white);

  ${({ active }) =>
    active &&
    css`
      animation: ${pulse} 2s ease-in-out infinite;
    `}

  &:hover {
    transform: scale(1.08);
    filter: brightness(1.15);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Status = styled.span`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
`;

const ActiveCount = styled.span`
  background: rgba(34, 197, 94, 0.25);
  border: 1px solid #22c55e;
  border-radius: 2rem;
  padding: 0.2rem 0.8rem;
  font-size: 1.2rem;
  color: #22c55e;
`;

const ErrorMsg = styled.p`
  font-size: 1.2rem;
  color: var(--color-red);
  max-width: 18rem;
  text-align: center;
`;

interface Props {
  isVoiceEnabled: boolean;
  isMuted: boolean;
  activePeers: string[];
  mutedPeers?: string[];
  toggleVoice: () => void;
  toggleMute: () => void;
  error: string | null;
}

const VoiceChat: React.FC<Props> = ({
  isVoiceEnabled,
  isMuted,
  activePeers,
  mutedPeers = [],
  toggleVoice,
  toggleMute,
  error,
}) => {
  return (
    <Wrapper>
      <Controls>
        <VoiceBtn
          active={isVoiceEnabled}
          onClick={toggleVoice}
          title={isVoiceEnabled ? "Leave voice chat" : "Join voice chat"}
        >
          {isVoiceEnabled ? "🎙" : "🎤"}
        </VoiceBtn>

        {isVoiceEnabled && (
          <VoiceBtn
            active={false}
            danger={isMuted}
            onClick={toggleMute}
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? "🔇" : "🔊"}
          </VoiceBtn>
        )}
      </Controls>

      {isVoiceEnabled ? (
        <Status>
          {isMuted ? "Muted" : "Voice active"}
          {activePeers.length > 0 && (
            <ActiveCount>
              {activePeers.length} connected
              {mutedPeers.length > 0 && ` · ${mutedPeers.length} muted`}
            </ActiveCount>
          )}
        </Status>
      ) : (
        <Status>Voice off</Status>
      )}

      {error && <ErrorMsg>{error}</ErrorMsg>}
    </Wrapper>
  );
};

export default VoiceChat;
